import { Button, ButtonProps, IconProps } from '@mui/material';
import { COLORS, MOTION, RADIUS } from '../../constants';
import { MaterialIcon } from '../materialIcon';
import { MaterialIconType } from '../materialIcon/types';
import { Transition } from '../transition';

export type MenuButtonModel = {
  icon?: MaterialIconType;
  text?: string;
  active?: boolean;
  iconStyles?: IconProps['sx'];
  iconSize?: number;
  // Groups consecutive menu items under a small label
  section?: string;
  title?: string;
  sx?: ButtonProps['sx'];
};

export const MenuButton = ({
  icon,
  text,
  active,
  iconStyles,
  iconSize,
  sx,
  ...rest
}: MenuButtonModel & ButtonProps) => {
  return (
    <Button
      data-testid="menu-button"
      variant="text"
      sx={{
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '8px',
        minHeight: 44,
        // Semilight, one step under the filled actions so they stay the heaviest text
        fontWeight: 400,
        color: active ? COLORS.text.brand : COLORS.text.main,
        width: '100%',
        borderRadius: `${RADIUS.lg}px`,
        backgroundColor: active ? COLORS.surface.brandTint : 'transparent',
        '&:hover': { backgroundColor: active ? COLORS.surface.brandTint : COLORS.background[0] },
        '& .MuiIcon-root': { color: COLORS.text.brand },
        // Brand bar on the left edge marks the current screen, so it doesn't read as one of the pill actions above
        ...(active && {
          fontWeight: 600,
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '25%',
            height: '50%',
            width: 3,
            borderRadius: `${RADIUS.full}px`,
            backgroundColor: COLORS.brand.main,
          },
        }),
        textAlign: 'left',
        ...sx,
      }}
      {...rest}
    >
      {icon && <MaterialIcon icon={icon} size={iconSize} sx={{ ...iconStyles }} />}
      {text && (
        <Transition type="fadeLeft" delay={MOTION.short / 1000}>
          {text}
        </Transition>
      )}
    </Button>
  );
};
